"use client";
const CookieUtil = {
  setCookie: (name, value, options = {}) => {
    if (!name || typeof name !== "string") {
      console.error("Cookie name must be a non-empty string.");
      return;
    }
    if (typeof value !== "string") {
      console.error("Cookie value must be a string.");
      return;
    }

    let cookieString = `${name}=${encodeURIComponent(value)}`;

    if (options.maxAge && typeof options.maxAge === "number") {
      cookieString += `; max-age=${options.maxAge}`;
    }

    if (options.path && typeof options.path === "string") {
      cookieString += `; path=${options.path}`;
    }
    if (options.secure === true) {
      cookieString += "; secure";
    }
    if (options.sameSite && typeof options.sameSite === "string") {
      cookieString += `; samesite=${options.sameSite}`;
    }
    if (process.env.NEXT_PUBLIC_IS_LOCAL_MACHINE !== "true") {
      const hardCodedDomain = ".fnp.com";
      cookieString += `; domain=${hardCodedDomain}`;
    }

    if (options.encode && typeof options.encode === "function") {
      cookieString = options.encode(cookieString);
    }
    if (typeof document !== "undefined") {
      document.cookie = cookieString;
    }
  },

  getCookie: (name) => {
    if (!name || typeof name !== "string") {
      console.error("Cookie name must be a non-empty string.");
      return null;
    }
    const nameEQ = name + "=";
    if (typeof document !== "undefined") {
      const ca = document.cookie.split(";");
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
      return null;
    }
  },

  deleteCookie: (name, options = {}) => {
    if (!name || typeof name !== "string") {
      console.error("Cookie name must be a non-empty string.");
      return;
    }

    let cookieString = `${name}=; max-age=-1`;

    if (options.path && typeof options.path === "string") {
      cookieString += `; path=${options.path}`;
    }
    if (process.env.NEXT_PUBLIC_IS_LOCAL_MACHINE !== "true") {
      const hardCodedDomain = ".fnp.com";
      cookieString += `; domain=${hardCodedDomain}`;
    }
    if (typeof document !== "undefined") {
      document.cookie = cookieString;
    }
  },
};

/**
 * Default options when persisting an SSR-provided id on the client (PLP, home, etc.).
 * @type {Readonly<{ maxAge: number, secure: boolean, sameSite: string, path: string, domain: string }>}
 */
export const DEFAULT_SSR_MIRROR_COOKIE_OPTIONS = Object.freeze({
  maxAge: 60 * 60 * 24 * 365, // 1 year
  secure: process.env.NODE_ENV === "production",
  sameSite: "lax",
  path: "/",
  domain: ".fnp.com",
});

/**
 * Sets a cookie from a server-provided value only when the browser has no non-empty value yet.
 * @param {string} name - Cookie name
 * @param {string} serverValue - Value from SSR (trimmed before set)
 * @param {Object} [options] - Overrides merged onto {@link DEFAULT_SSR_MIRROR_COOKIE_OPTIONS} for `setCookie`
 */
export function setCookieIfAbsent(name, serverValue, options = {}) {
  if (!serverValue || typeof serverValue !== "string" || serverValue.trim() === "") {
    return;
  }
  const existing = CookieUtil.getCookie(name);
  if (existing && existing.trim() !== "") {
    return;
  }
  CookieUtil.setCookie(name, serverValue.trim(), { ...DEFAULT_SSR_MIRROR_COOKIE_OPTIONS, ...options });
}

export default CookieUtil;
